import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Img from 'components/base/Img'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Link from 'components/base/Link'
import Title from 'components/base/Title'
import SectionRule from 'components/base/SectionRule/styles'
import IconGrid from 'components/base/Icon/Grid'
import { Theme as IconTheme } from 'components/base/Icon/Grid/Icon'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import AskQuestion from 'components/blocks/AskQuestion'
import InsuranceTypeIcon, { Theme as InsuranceTypeIconTheme } from 'components/base/Icon/InsuranceTypeIcon'
import Faq from 'components/blocks/Faq'

import MedicarePatient from 'assets/images/products/MedicarePatient.svg'
import MedicareType from 'assets/images/products/MedicareType.svg'

const ProductsMedicareInsurance: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Medicare Insurance | Insurance Lounge'
      description='Insurance Lounge is ready to help you find the perfect Medicare Insurance for your needs. Contact us today to speak with a qualified representative.'
      ogImg='/images/og-images/Medicare.webp'
    />
    <PageHero
      cta={true}
      ctaTitle='Get a medicare insurance quote now.'
      theme={PageHeroTheme.medicare}
      trackingInfo={{ page_category: 'product_page_medicare' }}
    >
      Medicare Insurance
    </PageHero>
    <Content short={true} margined={true} center={true}>
      <InsuranceTypeIcon theme={InsuranceTypeIconTheme.medicare} colored={true} hasMargin={true} />
      <Paragraph theme={ParagraphTheme.introLarge}>
        Medicare Insurance is a federal health insurance program that provides you with a variety of coverage types,
        including hospitalization and medical care. It’s essential to have the correct information about your benefits under
        Medicare to avoid confusion or overpaying for services that your plan may not cover.
      </Paragraph>
      <Title level={2}>Why Choose Insurance Lounge for Your Medicare Coverage?</Title>
      <Paragraph>
        Our team of Medicare insurance experts is committed to providing you with the best information possible about
        your benefits under <Link to='/products/medicare-insurance'>Medicare</Link>. We also offer various options
        that can help ensure you are covered no matter what life throws your way.
      </Paragraph>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={MedicareType}
          src2x={undefined}
          style={{
            height: 180,
            width: 'auto',
          }}
          alt='Medicare Type'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2}>Medicare Insurance Options</Title>
        <Paragraph>
          Here are some of your options for Medicare insurance:
        </Paragraph>
        <Title level={3} noMargin={true}>Original Medicare</Title>
        <Paragraph>
          Hospital insurance and medical insurance - Part A & B.
        </Paragraph>
        <Title level={3} noMargin={true}>Medicare Advantage</Title>
        <Paragraph>
          A bundled plan provided by insurers who are approved by the Medicare program. Includes hospital, medical,
          and prescription drugs and other benefits not covered with Original Medicare, such as vision, hearing, and dental.
        </Paragraph>
      </Content>
    </Content>

    <Content margined={true} center={true} short={true}>
      <Img
        src={MedicarePatient}
        src2x={undefined}
        style={{
          height: 220,
          width: 'auto',
        }}
        alt='Medicare Patient'
      />
    </Content>
    <Content center={true} short={true}>
      <Title level={2}>Applying for Medicare</Title>
      <Title level={3}>How to apply:</Title>
      <Paragraph>
        You can apply for Medicare Part A and/or Part B by submitting application materials either online or at your
        local Social Security office. It is important to note that failure to complete this step will delay your
        medicare coverage. What documents are needed for the application process?
      </Paragraph>
      <Paragraph>
        You will need to submit one of the following documents that contain your Social Security number:
      </Paragraph>
      <IconGrid
        icons={[
          {
            theme: IconTheme.birthCertificate,
            title: '',
          },
          {
            theme: IconTheme.driversLicense,
            title: '',
          },
          {
            theme: IconTheme.passport,
            title: '',
          },
          {
            theme: IconTheme.militaryID,
            title: '',
          },
        ]}
      />
      <Paragraph>You can apply for Medicare Part A and/or Part B at any time during the year. If you are already enrolled in Medicare,
        there is no waiting period between Parts A &amp; B. You may qualify for a special enrollment period if:
      </Paragraph>
      <IconGrid
        icons={[
          {
            theme: IconTheme.letterA,
            title: '',
          },
          {
            theme: IconTheme.letterB,
            title: '',
          },
          {
            theme: IconTheme.letterC,
            title: '',
          },
        ]}
      />
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content short={true} center={true}>
        <Title level={2}>Frequently Asked Questions</Title>
        <Faq
          items={[
            {
              question: 'When can I change my plan?',
              answer: (
                <Paragraph>
                  Enrollment for Medicare Advantage plans can be done at any time during the year.
                  You are only able to change your Original Medicare plan during Open Enrollment, which is
                  held each fall from October 15th - December 31st.
                </Paragraph>
              ),
            },
            {
              question: 'How do I check the status of my application?',
              answer: (
                <Paragraph>
                  You can check the status of your application by calling the Social Security Administration at (800) 772-1213.
                </Paragraph>
              ),
            },
            {
              question: 'How do I complain or appeal a Medicare decision?',
              answer: (
                <Paragraph>
                  If you have a concern or complaint with your Medicare coverage, the first step is to contact our office for assistance.
                  If we are unable to resolve it, you can file an appeal at <a href='http://www.medicare.gov' rel='noopener noreferrer' target='_blank'>www.Medicare.gov</a>.
                </Paragraph>
              ),
            },
            {
              question: 'Who do I contact for extra help?',
              answer: (
                <Paragraph>
                  If you live in Oregon and need help deciding if Medicare Insurance is best for you,
                  contact Insurance Lounge today by calling (541) 479-4975.
                </Paragraph>
              ),
            },
          ]}
        />
      </Content>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Title level={2}>Contact the Experts at Insurance Lounge</Title>
      <Paragraph>
        The <Link to='/about-us/our-team'>team at Insurance Lounge</Link> is well informed on the ins and outs of Medicare.
        For more information, fill out our <Link to='/quote/new'>quote and buy form</Link> to see if Medicare Insurance is right for you.
      </Paragraph>
    </Content>
    <SectionRule />
    <AskQuestion title='We’re Here, When You Need Us'>
      Reach out and an Insurance Concierge<sup>®</sup> will get in touch!
    </AskQuestion>
  </div>
)

export default ProductsMedicareInsurance
